import {
  CompanyRfuModelType,
  Orientation,
  SoftSkillCategory,
  SoftSkillFragment,
  SoftSkillGranularity,
} from 'generated/graphql';
import {
  formatCategory,
  formatGranularity,
  formatOrientation,
  isBroad,
  TRequiredSelectOption,
} from 'views/StaticContent/SoftSkills/SoftSkills.helpers';
import * as yup from 'yup';
import { TSelectOption } from '@spotted-zebra-uk/ui-components';

export const softSkillCategoryOptions: TSelectOption<SoftSkillCategory>[] = [
  {
    label: formatCategory(SoftSkillCategory.RightForUs),
    value: SoftSkillCategory.RightForUs,
  },
  {
    label: formatCategory(SoftSkillCategory.RightForTheRole),
    value: SoftSkillCategory.RightForTheRole,
  },
  {
    label: formatCategory(SoftSkillCategory.RightForTheFuture),
    value: SoftSkillCategory.RightForTheFuture,
  },
  {
    label: formatCategory(SoftSkillCategory.CognitiveSummary),
    value: SoftSkillCategory.CognitiveSummary,
  },
] as const;

export const softSkillGranularityOptions: TSelectOption<SoftSkillGranularity>[] =
  [
    {
      label: formatGranularity(SoftSkillGranularity.Focused),
      value: SoftSkillGranularity.Focused,
    },
    {
      label: formatGranularity(SoftSkillGranularity.Broad),
      value: SoftSkillGranularity.Broad,
    },
  ] as const;

export type TUpsertSoftSkillArgs = {
  trait: TRequiredSelectOption<number>;
  orientation: TRequiredSelectOption<Orientation>;
  weight: number;
};

export type TEditSoftSkillFormValues = {
  _id: number;
  subId: string;
  name: string;
  description?: string;
  owner: TRequiredSelectOption<number | null>;
  category: TRequiredSelectOption<SoftSkillCategory>;
  granularity: string;
  taxonomyVersion: number;
  softSkillTraits: TUpsertSoftSkillArgs[];
  focusedSoftSkills: TRequiredSelectOption<string>[];
  broadSoftSkills: TRequiredSelectOption<string>[];
  similarSoftSkills: TRequiredSelectOption<string>[];
  examples?: string[];
};

export const editSoftSkillValidationSchema = yup.object().shape({
  name: yup
    .string()
    .ensure()
    .min(3, "Soft skill name can't be shorter than 3 characters.")
    .max(40, "Soft skill name can't be longer than 40 characters.")
    .required('Soft skill name is required')
    .test(
      'name-whitespace',
      'Name must not have leading or trailing whitespace.',
      softSkillName => {
        return !softSkillName.startsWith(' ') && !softSkillName.endsWith(' ');
      }
    ),
  description: yup
    .string()
    .min(10, "Description can't be shorter than 10 characters.")
    .max(200, "Description can't be longer than 200 characters.")
    .test(
      'description-whitespace',
      'Description must not have leading or trailing whitespace.',
      description => {
        if (!description) return true;
        return !description.startsWith(' ') && !description.endsWith(' ');
      }
    ),
  taxonomyVersion: yup
    .number()
    .integer()
    .min(1, 'Minimum version number is 1.')
    .max(2, 'Maximum version number is 2.'),
  softSkillTraits: yup
    .array()
    .of(
      yup.object().shape({
        weight: yup
          .number()
          .min(0, 'Weight must be >= 0.')
          .max(1, 'Weight must be <= 1.')
          .required('Weight is required.'),
        orientation: yup.object().shape({
          label: yup.string().required(),
          value: yup
            .mixed<Orientation>()
            .oneOf(Object.values(Orientation), 'Orientation is invalid.')
            .required('Orientation is required.'),
        }),
      })
    )
    .required()
    .test(
      'sum-of-weights',
      'Sum of trait weights must add up to 1.',
      softSkillTraits => {
        if (!softSkillTraits?.length) return true;
        const sum =
          softSkillTraits?.reduce(
            (acc, curr) => acc + (curr?.weight ?? 0),
            0
          ) ?? 0;
        return Number(sum.toFixed(4)) === 1;
      }
    ),
  focusedSoftSkills: yup
    .array()
    .of(
      yup.object().shape({
        label: yup.string(),
        value: yup.string().required('Must not leave relation empty.'),
      })
    )
    .required(),
  broadSoftSkills: yup
    .array()
    .of(
      yup.object().shape({
        label: yup.string(),
        value: yup.string().required('Must not leave relation empty.'),
      })
    )
    .required('Must not leave relation empty'),
  category: yup.object().shape({
    label: yup.string().required(),
    value: yup
      .mixed<SoftSkillCategory>()
      .oneOf(Object.values(SoftSkillCategory))
      .required(),
  }),
  examples: yup
    .array()
    .of(
      yup.string().test('non-empty', 'Example cannot be empty.', value => {
        if (!value) return false;
        return value.trim().length > 0;
      })
    )
    .required(),
  similarSoftSkills: yup.array().of(
    yup.object().shape({
      label: yup.string().required('This field is required.'),
      value: yup.string().required('This field is required.'),
    })
  ),
});

export const getEditSoftSkillInitialValues = (
  softSkill: SoftSkillFragment | undefined
): TEditSoftSkillFormValues => {
  return {
    _id: softSkill?.id ?? -1,
    subId: softSkill?.subId ?? '',
    name: softSkill?.name ?? '',
    owner: {
      label: softSkill?.company?.name ?? 'Global',
      value: softSkill?.company?.id ?? null,
    },
    taxonomyVersion: softSkill?.taxonomyVersion ?? 1,
    description: softSkill?.description ?? '',
    category: {
      label: formatCategory(
        softSkill?.category ?? SoftSkillCategory.RightForTheRole
      ),
      value: softSkill?.category ?? SoftSkillCategory.RightForTheRole,
    },
    granularity: isBroad(softSkill)
      ? formatGranularity(SoftSkillGranularity.Broad)
      : formatGranularity(SoftSkillGranularity.Focused),
    focusedSoftSkills: isBroad(softSkill)
      ? softSkill.focusedSoftSkills.map(({ name, subId }) => ({
          label: name,
          value: subId,
        }))
      : [],
    broadSoftSkills:
      softSkill?.__typename === 'FocusedSoftSkill'
        ? softSkill.broadSoftSkills.map(({ name, subId }) => ({
            label: name,
            value: subId,
          }))
        : [],
    similarSoftSkills:
      softSkill?.__typename === 'FocusedSoftSkill' ||
      softSkill?.__typename === 'BroadSoftSkill'
        ? softSkill.similarSoftSkills.map(({ name, subId }) => ({
            label: name,
            value: subId,
          }))
        : [],
    softSkillTraits:
      softSkill?.softSkillTraits?.map(({ trait, orientation, weight }) => ({
        trait: { label: trait?.name ?? '', value: trait?.id ?? -1 },
        weight,
        orientation: {
          label: formatOrientation(orientation),
          value: orientation,
        },
      })) ?? [],
    examples: softSkill?.examples ?? [],
  };
};

export type TAddSoftSkillFormValues = {
  name?: string;
  description?: string;
  owner: TRequiredSelectOption<number | null>;
  category?: TRequiredSelectOption<SoftSkillCategory>;
  granularity?: TRequiredSelectOption<SoftSkillGranularity>;
  taxonomyVersion: number;
};

export const addSoftSkillValidationSchema = yup.object().shape({
  name: yup
    .string()
    .ensure()
    .min(3, "Soft skill name can't be shorter than 3 characters.")
    .max(40, "Soft skill name can't be longer than 40 characters.")
    .required('Soft skill name is required')
    .test(
      'name-whitespace',
      'Name must not have leading or trailing whitespace.',
      softSkillName => {
        return !softSkillName.startsWith(' ') && !softSkillName.endsWith(' ');
      }
    ),
  description: yup
    .string()
    .min(10, "Description can't be shorter than 10 characters.")
    .max(200, "Description can't be longer than 200 characters.")
    .test(
      'description-whitespace',
      'Description must not have leading or trailing whitespace.',
      description => {
        if (!description) return true;
        return !description.startsWith(' ') && !description.endsWith(' ');
      }
    ),
  taxonomyVersion: yup
    .number()
    .integer()
    .min(1, 'Minimum version number is 1.')
    .max(2, 'Maximum version number is 2.'),
  category: yup.object().shape({
    label: yup.string(),
    value: yup
      .mixed<SoftSkillCategory>()
      .oneOf(Object.values(SoftSkillCategory))
      .required('You must select a category.'),
  }),
  granularity: yup.object().shape({
    label: yup.string(),
    value: yup
      .mixed<SoftSkillGranularity>()
      .oneOf(Object.values(SoftSkillGranularity))
      .required('You must select a granularity.'),
  }),
});

export const getAddSoftSkillInitialValues = (): TAddSoftSkillFormValues => {
  return {
    name: undefined,
    description: undefined,
    owner: {
      label: 'Global',
      value: -1,
    },
    taxonomyVersion: 2,
    category: undefined,
    granularity: undefined,
  };
};

export const softSkillRFUModelTypeLabel = {
  [CompanyRfuModelType.BehaviourBased]: 'Behaviour based',
  [CompanyRfuModelType.ValueBased]: 'Value based',
};

export const getRelationValues = (
  values: TEditSoftSkillFormValues,
  relationType: 'vertical' | 'horizontal',
  isBroadSoftSkill: boolean
): { label: string; value: string }[] => {
  if (relationType === 'horizontal') {
    return values.similarSoftSkills;
  }
  return isBroadSoftSkill ? values.focusedSoftSkills : values.broadSoftSkills;
};

interface FormErrors {
  [key: string]: string | FormErrors | (string | FormErrors)[];
}

/**
 * Formats form validation errors for the editSoftSkillValidationSchema
 * @param errors - The form errors object from the soft skill validation
 * @returns A formatted string with all errors listed in a readable format
 */
export const formatSoftSkillFormErrors = (errors: FormErrors): string => {
  const errorMessages: string[] = [];

  // Handle specific fields from the editSoftSkillValidationSchema
  if (errors.name) {
    errorMessages.push(`• Name: ${errors.name}`);
  }

  if (errors.description) {
    errorMessages.push(`• Description: ${errors.description}`);
  }

  if (errors.taxonomyVersion) {
    errorMessages.push(`• Taxonomy Version: ${errors.taxonomyVersion}`);
  }

  if (errors.category) {
    errorMessages.push(`• Category: ${errors.category}`);
  }

  if (errors.examples) {
    if (Array.isArray(errors.examples)) {
      errors.examples.forEach((error, index) => {
        if (error) {
          errorMessages.push(`• Example ${index + 1}: ${error}`);
        }
      });
    } else if (typeof errors.examples === 'string') {
      errorMessages.push(`• Examples: ${errors.examples}`);
    }
  }

  if (errors.softSkillTraits) {
    if (Array.isArray(errors.softSkillTraits)) {
      errors.softSkillTraits.forEach((traitError, index) => {
        if (traitError && typeof traitError === 'object') {
          if (traitError.weight) {
            errorMessages.push(
              `• Trait ${index + 1} Weight: ${traitError.weight}`
            );
          }
          if (traitError.orientation) {
            errorMessages.push(
              ` • Trait ${index + 1} Orientation: ${traitError.orientation}`
            );
          }
        }
      });
    } else if (typeof errors.softSkillTraits === 'string') {
      errorMessages.push(`• Traits: ${errors.softSkillTraits}`);
    }
  }

  if (errors.focusedSoftSkills) {
    if (Array.isArray(errors.focusedSoftSkills)) {
      errors.focusedSoftSkills.forEach((error, index) => {
        if (error && typeof error === 'object') {
          if (error.value) {
            errorMessages.push(
              `• Focused Soft Skill ${index + 1}: ${error.value}`
            );
          }
        }
      });
    } else if (typeof errors.focusedSoftSkills === 'string') {
      errorMessages.push(`• Focused Soft Skills: ${errors.focusedSoftSkills}`);
    }
  }

  if (errors.broadSoftSkills) {
    if (Array.isArray(errors.broadSoftSkills)) {
      errors.broadSoftSkills.forEach((error, index) => {
        if (error && typeof error === 'object') {
          if (error.value) {
            errorMessages.push(
              `• Broad Soft Skill ${index + 1}: ${error.value}`
            );
          }
        }
      });
    } else if (typeof errors.broadSoftSkills === 'string') {
      errorMessages.push(`• Broad Soft Skills: ${errors.broadSoftSkills}`);
    }
  }

  if (errors.similarSoftSkills) {
    if (Array.isArray(errors.similarSoftSkills)) {
      errors.similarSoftSkills.forEach((error, index) => {
        if (error && typeof error === 'object') {
          if (error.label) {
            errorMessages.push(
              `• Similar Soft Skill ${index + 1} Label: ${error.label}`
            );
          }
          if (error.value) {
            errorMessages.push(
              `• Similar Soft Skill ${index + 1} Value: ${error.value}`
            );
          }
        }
      });
    } else if (typeof errors.similarSoftSkills === 'string') {
      errorMessages.push(`• Similar Soft Skills: ${errors.similarSoftSkills}`);
    }
  }

  // Handle any other unexpected errors
  Object.entries(errors).forEach(([key, value]) => {
    if (
      ![
        'name',
        'description',
        'taxonomyVersion',
        'category',
        'examples',
        'softSkillTraits',
        'focusedSoftSkills',
        'broadSoftSkills',
        'similarSoftSkills',
      ].includes(key)
    ) {
      if (typeof value === 'string') {
        errorMessages.push(`• ${formatFieldName(key)}: ${value}`);
      }
    }
  });

  if (errorMessages.length === 0) {
    return 'Please check your form for errors.';
  }

  if (errorMessages.length === 1) {
    return errorMessages[0].replace('• ', '');
  }

  return `${errorMessages.join('\n')}`;
};

/**
 * Formats field names to be more user-friendly
 * @param fieldName - The raw field name from the form
 * @returns A formatted, human-readable field name
 */
const formatFieldName = (fieldName: string): string => {
  // Remove array indices and convert to readable format
  const cleanName = fieldName
    .replace(/\[\d+\]/g, '') // Remove array indices like [0], [1]
    .replace(/\./g, ' ') // Replace dots with spaces
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^\w/, c => c.toUpperCase()) // Capitalize first letter
    .trim();

  return cleanName;
};
